import type {
  ActivityFeedEntry,
  ActivityFeedResponse,
  ActivityFeedSourceReference,
  ActivityJournalEntryType,
  ActivitySubjectType,
  TransactionKind,
  TransactionStatus
} from "@salesko/contracts";
import type { Dictionary } from "../../i18n";

type ActivityFeedCopy = Dictionary["activityFeed"];

export type ActivityFeedPagesProjection = {
  items: ActivityFeedEntry[];
  error?: string;
};

export const ACTIVITY_FEED_DUPLICATE_PAGE_ERROR =
  "Activity Feed returned a duplicate entry. Previously verified activity is still shown.";
export const ACTIVITY_FEED_ORDER_ERROR =
  "Activity Feed returned entries out of order. Previously verified activity is still shown.";

type AvailableActivitySource = ActivityFeedSourceReference & {
  evidenceState: "available";
  evidenceSpanId: string;
  sourceFrameRecordId: string;
};

/**
 * Projects infinite-query pages without re-sorting the server-owned journal.
 * Each page is accepted atomically: a corrupt page cannot partially replace or
 * append to the previously verified prefix.
 */
export function activityFeedPagesProjection(pages: ActivityFeedResponse[]): ActivityFeedPagesProjection {
  const items: ActivityFeedEntry[] = [];
  const verifiedIds = new Set<string>();

  for (const page of pages) {
    const pageIds = new Set<string>();
    let previous = items.at(-1);

    for (const item of page.items) {
      if (verifiedIds.has(item.id) || pageIds.has(item.id)) {
        return { error: ACTIVITY_FEED_DUPLICATE_PAGE_ERROR, items };
      }
      if (previous && !isStrictlyBeforeInFeed(previous, item)) {
        return { error: ACTIVITY_FEED_ORDER_ERROR, items };
      }
      pageIds.add(item.id);
      previous = item;
    }

    for (const item of page.items) {
      verifiedIds.add(item.id);
      items.push(item);
    }
  }

  return { items };
}

export function activityEntryTypeLabel(entryType: ActivityJournalEntryType, copy: ActivityFeedCopy): string {
  switch (entryType) {
    case "event.occurred":
      return copy.entryTypes.eventOccurred;
    case "opportunity.surfaced":
      return copy.entryTypes.opportunitySurfaced;
    case "transaction.recorded":
      return copy.entryTypes.transactionRecorded;
    case "transaction.status_changed":
      return copy.entryTypes.transactionStatusChanged;
    case "transaction.corrected":
      return copy.entryTypes.transactionCorrected;
  }
}

export function activitySubjectTypeLabel(subjectType: ActivitySubjectType, copy: ActivityFeedCopy): string {
  switch (subjectType) {
    case "event":
      return copy.subjectTypes.event;
    case "opportunity":
      return copy.subjectTypes.opportunity;
    case "transaction":
      return copy.subjectTypes.transaction;
  }
}

export function activityEvidenceStateLabel(
  evidenceState: ActivityFeedSourceReference["evidenceState"],
  copy: ActivityFeedCopy
): string {
  switch (evidenceState) {
    case "available":
      return copy.evidenceStates.available;
    case "source_unavailable":
      return copy.evidenceStates.sourceUnavailable;
    case "redacted":
      return copy.evidenceStates.redacted;
  }
}

export function transactionKindLabel(kind: TransactionKind, copy: ActivityFeedCopy): string {
  return copy.transactionKinds[kind];
}

export function transactionStatusLabel(status: TransactionStatus, copy: ActivityFeedCopy): string {
  return copy.transactionStatuses[status];
}

export function formatMinorUnitAmount(amountMinor: number, currency: string, locale: string): string {
  if (!Number.isSafeInteger(amountMinor)) {
    throw new RangeError("Transaction amountMinor must be a safe integer.");
  }

  const formatter = new Intl.NumberFormat(locale, { currency, style: "currency" });
  const fractionDigits = formatter.resolvedOptions().maximumFractionDigits ?? 0;
  const divisor = 10n ** BigInt(fractionDigits);
  const amount = BigInt(amountMinor);
  const major = amount / divisor;

  if (fractionDigits === 0) {
    return formatter.format(Number(major));
  }

  const fraction = (amount % divisor).toString().padStart(fractionDigits, "0");
  return formatter
    .formatToParts(Number(major))
    .map((part) => (part.type === "fraction" ? fraction : part.value))
    .join("");
}

export function isAvailableActivitySource(
  source: ActivityFeedSourceReference
): source is AvailableActivitySource {
  return (
    source.evidenceState === "available" &&
    source.evidenceSpanId !== undefined &&
    source.sourceFrameRecordId !== undefined
  );
}

function isStrictlyBeforeInFeed(previous: ActivityFeedEntry, current: ActivityFeedEntry): boolean {
  const previousRecordedAt = Date.parse(previous.recordedAt);
  const currentRecordedAt = Date.parse(current.recordedAt);
  return previousRecordedAt > currentRecordedAt || (previousRecordedAt === currentRecordedAt && previous.id > current.id);
}
